/**
 * 이상한 방 - 싱글 튜토리얼 진행 저장
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 9.5절(싱글의 포지션 = 튜토리얼) · 단계 정의는 `levels.ts`
 *
 * ## 계약: 저장은 실패해도 된다, 게임은 실패하면 안 된다
 *
 * `localStorage`는 **접근하는 것만으로 던진다.** 사생활 보호 모드, 쿠키/사이트데이터 차단,
 * iframe의 서드파티 저장소 차단, 용량 초과(QuotaExceededError)가 전부 그 경로다.
 * 여기서 예외가 새어 나가면 "브라우저 설정 때문에 게임이 아예 안 켜진다"가 되는데,
 * 진행 저장은 편의 기능이고 게임은 콘텐츠다. **편의가 콘텐츠를 죽이면 안 된다.**
 *
 * 그래서 이 파일의 규칙은 하나다: **밖으로 던지지 않는다.**
 *  - 읽기 실패 → 빈 진행(1단계만 해금)으로 계속 간다
 *  - 쓰기 실패 → `false`를 돌려준다. 호출부는 "저장되지 않았다"를 표시할 수는 있지만 멈추지 않는다
 *
 * ## 시계를 읽지 않는다
 *
 * 최단 시간은 `Date.now()`가 아니라 **프레임 델타로 누적된 경과(ms)**를 인자로 받는다.
 * 게임 상태 생성 경로에서 시계를 읽지 않는다는 규칙(rng.ts 상단)을 진행 저장도 그대로 따른다 -
 * 탭이 백그라운드로 갔다 오면 벽시계 차이와 실제 플레이 시간이 갈리므로 기록으로도 부정확하다.
 *
 * ## 저장 포맷에 버전이 있는 이유 (v1 → v2에서 처음으로 쓰였다)
 *
 * 단계 표는 앞으로 바뀐다(9.5절의 튜토리얼은 플레이테스트 대상이다). 포맷이 바뀔 때
 * 버전이 없으면 남의 브라우저에 남은 옛 데이터를 **읽을 수도 지울 수도 없다.**
 *
 * v2에서 계정 탄창(`magazines.ts`)이 이 레코드에 얹혔다. v1 저장에는 그 필드가 아예 없는데,
 * 버전만 보고 통째로 버리면 **이미 깬 단계가 전부 잠긴다.** 그건 기능 추가가 아니라 데이터 손실이다.
 * 그래서 마이그레이션은 필드 단위다 - 단계 기록은 v1과 v2가 같은 모양이라 그대로 통과시키고
 * (`migrateLevels`), 탄창만 새로 채운다(`migrateMagazines`). 자리만 비워 뒀던 함수가
 * 여기서 처음으로 일을 한다.
 *
 * ## 필드를 더할 때 버전을 올리지 않는 경우가 있다
 *
 * 최고 기록에 `bestFound`·`bestChangeCount`가 얹히면서 단계 기록의 모양이 바뀌었는데
 * **버전은 그대로 2다.** 기준은 하나다 - **읽는 쪽이 채워 넣어야 하는 값인가.**
 *
 * 탄창(v1 → v2)은 채워야 했다. 없으면 0이 되고, 0은 "탄창이라는 기능을 못 써 본 사람"이라
 * 초기 지급으로 메워야 기존 플레이어가 손해를 안 본다. 반면 이 둘은 채울 것이 없다 -
 * 옛 저장에 없다는 사실 자체가 `null`("그때는 안 적었다")이라는 정확한 값이고, 화면은
 * 그 경우 점수만 말하면 된다(`ui/levelSelect.ts`의 `LevelBest`).
 *
 * 그런데도 올리면 잃는 것이 있었다 - v3 전까지 `migrateLevels`는 모르는(= 더 큰) 버전을 `null`로
 * 떨어뜨렸으므로, 배포 직전에 열어 둔 옛 탭이 새 저장을 읽는 순간 진행이 통째로 빈 것이 됐다.
 * 채울 것도 없는 변경에 그 위험을 지불할 이유가 없었다.
 *
 * ## 미래 버전의 단계 기록은 레코드 단위로 살린다 (v3 직전에 바뀐 규칙)
 *
 * 위 위험은 v3(핀·봉투·사물함)를 올리면서 실제 문제가 됐다 - 올릴 이유가 분명한 변경인데 옛 탭 하나가
 * 진행을 비우는 사고는 그대로였다. 그래서 규칙을 바꿨다: **모르는 버전이라도 `levels`는 레코드 단위로
 * 모양을 검사해 맞는 것만 살린다**(`parseLevelRecord`). "조용히 틀린 진행"을 막던 것은 사실 버전 번호가
 * 아니라 모양 검증이었고, 그 검증은 언제나 있었다. 단계 기록의 모양이 실제로 바뀌는 날에는 그때의
 * 마이그레이션이 그 버전 번호를 보고 갈라 준다. 형제 필드(탄창·핀·봉투·사물함)는 각자의 파서가
 * 모르는 모양을 새 계정 상태로 떨어뜨린다.
 *
 * ## v3 - 핀 · 봉투 · 사물함 · 계정 시드
 *
 * 로드맵 M3. 이 넷은 전부 **읽는 쪽이 채워 넣어야 하는 값**이라 버전 승격 조건에 맞는다.
 * 핀 0·빈 봉투 상태·빈 사물함은 새 계정과 같은 출발선이고, 계정 시드는 **주입받은 `mint()`**로 만든다 -
 * 이 파일은 난수도 시계도 읽지 않으므로(`rng.ts` 상단의 규칙) 엔트로피는 `app/accountSeed.ts`에서 온다.
 * 기본 `mint`는 빈 문자열을 돌려주는 자리표시자다(`UNMINTED_ACCOUNT_SEED`). 빈 시드로도 봉투는 결정론적으로
 * 열리지만 모두가 같은 스트림을 보게 되므로, 앱은 반드시 진짜 `mint`를 넘긴다(`main.ts`). 저장에 빈 시드가
 * 남아 있어도 다음 읽기에서 다시 `mint()`를 부르므로 한 번 배선되면 저절로 고쳐진다.
 *
 * ## 매일의 방은 v3에 얹히되 **버전을 올리지 않는다** (M9)
 *
 * `daily`(마지막 출석 · 연속 일수 · 그날의 기록)는 위의 "필드를 더할 때 버전을 올리지 않는 경우"에 정확히 해당한다 -
 * 옛 저장에 없다는 사실 자체가 "아직 한 번도 안 했다"라는 정확한 값이고, 빈 상태(`daily.createDailyState`)가
 * 새 계정과 같은 출발선이다. 채울 것이 없으므로 올릴 이유가 없고, 올리면 배포 직전에 열어 둔 옛 탭이 치르는
 * 위험만 남는다(위 절). 값의 규칙은 전부 `game/daily.ts`에 있고 여기서는 파서를 부르고 갈아 끼울 뿐이다.
 *
 * ## 왜 탄창이 진행 저장에 얹히는가 (핀·봉투·사물함·매일의 방도 같은 이유)
 *
 * 키를 따로 두면 저장이 **부분적으로** 실패할 수 있기 때문이다 - 한쪽만 써지는 순간
 * "탄창은 줄었는데 클리어는 기록되지 않은" 상태가 남고, 그 불일치를 나중에 고칠 방법이 없다.
 * 봉투는 더 심하다 - "핀은 빠졌는데 산출물은 안 들어온" 상태는 곧바로 문의가 된다.
 * 한 덩어리로 쓰면 실패는 전부 아니면 전무다.
 */

import { createDailyState, parseDailyState, type DailyState } from './daily'
import {
  createEnvelopeState,
  parseEnvelopeState,
  type EnvelopeState,
} from './economy/envelope'
import { createInventory, parseInventory, type Inventory } from './economy/inventory'
import { normalizePins } from './economy/pins'
import { FIRST_LEVEL_ID, getLevel, meetsClearCondition, type LevelDef } from './levels'
import {
  createMagazineState,
  parseMagazineState,
  type MagazineState,
} from './magazines'

/* --- 상수 ------------------------------------------------------------------- */

/**
 * 저장 키. **버전을 키에 넣지 않는다** - 키에 버전이 붙으면 포맷이 바뀔 때 옛 데이터가
 * 다른 키에 남아 마이그레이션 대상이 아니라 그냥 쓰레기가 된다. 버전은 값 안에 있다.
 */
export const PROGRESS_STORAGE_KEY = 'weird-room:progress'

/**
 * 현재 저장 포맷 버전. 포맷을 바꿀 때 올리고 마이그레이션 함수에 단계를 추가한다.
 *
 *  - v1: 단계 기록만
 *  - v2: 계정 탄창(`magazines`)이 추가됨. 단계 기록의 모양은 그대로다.
 *  - v3: 계정 시드(`accountSeed`) · 핀(`pins`) · 봉투 상태(`envelope`) · 사물함(`inventory`). 단계 기록의 모양은 그대로다.
 *
 * M9의 `daily`는 v3에 얹히되 **버전을 올리지 않았다**(머리말 "매일의 방은 v3에 얹히되") - 빈 상태가 새 계정과
 * 같은 출발선이라 읽는 쪽이 채워 넣을 값이 없다.
 */
export const PROGRESS_VERSION = 3

/** 탄창이 없던 마지막 버전. 이 이하의 저장은 탄창을 새로 채워 준다. */
const VERSION_BEFORE_MAGAZINES = 1

/** 첫 버전. 이보다 작은 버전 번호는 우리가 쓴 적이 없다 - 그런 저장은 통째로 남의 것이다. */
const FIRST_VERSION = 1

/**
 * 계정 시드가 아직 발급되지 않았음을 뜻하는 값. 기본 `mint`가 이것을 돌려준다.
 * 앱은 `app/accountSeed.ts`의 진짜 `mint`를 넘기고, 이 값이 저장에 남아 있으면 다음 읽기가 다시 발급한다.
 */
export const UNMINTED_ACCOUNT_SEED = ''

/** 계정 시드 발급기. 이 파일은 엔트로피를 쓰지 않으므로 밖에서 받는다. */
export type AccountSeedMint = () => string

const UNMINTED: AccountSeedMint = () => UNMINTED_ACCOUNT_SEED

/** 단계 키는 십진수 문자열만 허용한다. `__proto__` 같은 키가 섞여 들어오는 경로를 이 정규식이 닫는다. */
const LEVEL_KEY_PATTERN = /^\d+$/

/** 아직 한 번도 깨지 않은 단계의 최단 시간. `null`은 "기록 없음"이지 0이 아니다. */
const NO_TIME: null = null

/**
 * 아직 적어 두지 않은 값. 옛 저장에는 이 필드 자체가 없다.
 * **0이 아니다** - "0개를 찾았다"와 "그때는 안 적었다"는 화면에서 다른 말이어야 한다.
 */
const NOT_RECORDED: null = null

/* --- 타입 ------------------------------------------------------------------- */

/**
 * 단계 하나의 기록.
 *
 * `bestTimeMs`는 **클리어한 판에서만** 갱신된다. 실패는 언제나 빠르므로
 * (첫 오답 여섯 번이면 끝난다) 실패 시간을 최단 기록으로 받으면 기록표가 거짓이 된다.
 */
export type LevelRecord = Readonly<{
  cleared: boolean
  bestScore: number
  bestTimeMs: number | null
  /**
   * **최고 점수를 낸 그 판의** 발견 수. 최댓값이 아니라 짝이다 -
   * 각각 다른 판에서 온 숫자를 한 줄에 늘어놓으면 그 줄은 존재한 적 없는 판을 말하게 된다.
   */
  bestFound: number | null
  /**
   * 그 판의 이상한 곳 수. 함께 굳히는 이유는 튜닝이 단계의 개수를 바꾸기 때문이다 -
   * 오늘의 값으로 옛 기록을 나누면 "12개 중 12개"였던 기록이 "9개 중 12개"가 된다.
   */
  bestChangeCount: number | null
  /** 시도 횟수. 어느 단계에서 사람들이 막히는지를 보는 유일한 지표다. */
  plays: number
}>

/** 저장되는 진행 전체. 단계 키는 십진수 문자열(`'1'`, `'2'`, …)이다. */
export type Progress = Readonly<{
  version: number
  levels: Readonly<Record<string, LevelRecord>>
  /** 계정이 들고 있는 탄창(`magazines.ts`). 라운드가 아니라 사람에게 붙는 값이다. */
  magazines: MagazineState
  /**
   * 봉투 스트림의 뿌리(`economy/envelope.ts`). 첫 실행에 한 번 만들어지고 그 뒤로 바뀌지 않는다.
   * `UNMINTED_ACCOUNT_SEED`면 아직 발급 전이다(`hasAccountSeed`).
   */
  accountSeed: string
  /** 보유 핀(`economy/pins.ts`). 0 이상 `PIN_CAP` 이하의 정수다. */
  pins: number
  /** 봉투 회차·피티·최근 이력(`economy/envelope.ts`). */
  envelope: EnvelopeState
  /** 사물함 - 보유·장착·준비물 수(`economy/inventory.ts`). */
  inventory: Inventory
  /**
   * 매일의 방 - 마지막 출석·연속 일수·그날의 기록(`game/daily.ts`). **단계 기록과 다른 표다**(로드맵 M9 규칙 2) -
   * 매일의 방은 단계가 아니라 날짜로 세는 것이라 섞으면 "1단계 최고 기록"이 어느 날의 것인지 알 수 없게 된다.
   */
  daily: DailyState
  /**
   * 여럿이 바꾸기 안내(`ui/changerCoach.ts`)를 끝냈는가. **계정의 것이라 여기 산다** - 판마다 다시 물으면
   * 두 번째 판에서 아는 사람에게 같은 말을 또 하게 된다.
   *
   * 불리언 하나인 이유는 안내가 하나뿐이기 때문이다. 둘째가 생기는 날 목록으로 넓히면 되고, 그때까지
   * 목록을 미리 두면 "무엇이 들어 있어야 맞는가"를 아무도 답할 수 없는 필드가 하나 늘 뿐이다.
   *
   * 버전을 올리지 않았다 - **읽는 쪽이 채워 넣을 값이 없다**(머리말의 기준). 필드가 없다는 사실 자체가
   * `false`("아직 안 봤다")라는 정확한 값이고, 이미 여럿이를 해 본 사람이 한 번 더 보는 것은 손해가 아니다.
   */
  coached: boolean
}>

/** 라운드 밖에서 바뀌는 경제 값 셋. 봉투 한 장이 이 셋을 한 번에 움직이므로 한 패치로 받는다. */
export type EconomyPatch = Readonly<Partial<Pick<Progress, 'pins' | 'envelope' | 'inventory'>>>

/** 한 판의 결과. 시간은 프레임 누적 경과(ms)이며 시계에서 오지 않는다. */
export type LevelOutcome = Readonly<{
  foundCount: number
  score: number
  elapsedMs: number
  /**
   * 이 판의 이상한 곳 수. **없어도 된다** - 모르면 그 자리만 비운 채(`null`) 점수와 시간은
   * 그대로 기록한다. 필수로 두면 이 값을 모르는 호출부 하나 때문에 판 하나가 통째로
   * 기록되지 않고, 그건 "몇 개 중 몇 개"를 못 보는 것보다 나쁘다.
   */
  changeCount?: number
}>

/**
 * 저장소. `localStorage`가 구조적으로 이 모양이므로 그대로 꽂히고,
 * 테스트는 던지는 가짜 저장소를 넣어 실패 경로를 검사한다.
 */
export type ProgressStorage = Readonly<{
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
  removeItem: (key: string) => void
}>

/* --- 저장소 접근 -------------------------------------------------------------- */

/**
 * 기본 저장소. **속성 접근 자체를 try/catch로 감싼다** -
 * 일부 브라우저는 `window.localStorage`를 *읽는 순간* SecurityError를 던진다.
 */
export function defaultProgressStorage(): ProgressStorage | null {
  try {
    const storage = globalThis.localStorage as ProgressStorage | undefined
    return storage ?? null
  } catch {
    return null
  }
}

/* --- 순수 헬퍼 --------------------------------------------------------------- */

function levelKey(levelId: number): string {
  return String(levelId)
}

/**
 * 유한한 0 이상의 수만 통과시킨다. 아니면 `null`.
 * NaN·음수·문자열이 기록을 오염시키는 경로가 전부 이 한 곳으로 모인다 -
 * 한 번 저장된 `NaN`은 `JSON.stringify`에서 `null`이 되고, 그 뒤로는 원인을 찾을 수 없다.
 */
function finiteNonNegative(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null
}

/** `finiteNonNegative`의 기본값 버전. 점수·횟수처럼 "없으면 0"인 값에 쓴다. */
function toNonNegative(value: unknown, fallback: number): number {
  return finiteNonNegative(value) ?? fallback
}

/**
 * 개수로 읽는다. 없거나 수가 아니면 `null`("안 적었다")이지 0이 아니다.
 * 0으로 접으면 옛 저장이 "0개를 찾은 판"으로 되살아난다.
 */
function toCountOrNull(value: unknown): number | null {
  const count = finiteNonNegative(value)
  return count === null ? NOT_RECORDED : Math.floor(count)
}

function isRecordLike(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * 아무것도 하지 않은 사람의 진행.
 *
 * 탄창은 **0이 아니라 초기 지급량으로 시작한다**(`magazines.createMagazineState`).
 * 이 함수가 저장 실패의 폴백이기도 하다는 점이 중요하다 - 사생활 보호 모드에서 매번 여기로
 * 떨어지는 사람도 탄창이라는 기능이 존재한다는 것 정도는 볼 수 있어야 한다.
 */
export function createEmptyProgress(mint: AccountSeedMint = UNMINTED): Progress {
  return {
    version: PROGRESS_VERSION,
    levels: {},
    magazines: createMagazineState(),
    accountSeed: mint(),
    pins: 0,
    envelope: createEnvelopeState(),
    inventory: createInventory(),
    daily: createDailyState(),
    coached: false,
  }
}

/** 지금 들고 있는 탄창 수. 화면과 라운드 생성이 중첩 경로를 알 필요가 없게 한다. */
export function magazineCount(progress: Progress): number {
  return progress.magazines.count
}

/** 지금 들고 있는 핀. `magazineCount`와 같은 이유로 중첩 경로를 감춘다. */
export function pinCount(progress: Progress): number {
  return progress.pins
}

/** 계정 시드가 발급됐는가. 거짓이면 기본 `mint`로 읽힌 진행이다(`UNMINTED_ACCOUNT_SEED`). */
export function hasAccountSeed(progress: Progress): boolean {
  return progress.accountSeed !== UNMINTED_ACCOUNT_SEED
}

/**
 * 탄창만 바꾼 새 진행. 단계 기록은 손대지 않는다.
 *
 * 지급·소비 자체는 `magazines.ts`가 하고 여기서는 갈아 끼우기만 한다. 규칙을 두 벌 만들지 않기
 * 위해서다 - 상한·이력·미구현 경로 거부는 전부 그쪽 파일 하나에만 있어야 한다.
 *
 * 스프레드로 나머지를 흘려보낸다. 필드를 하나씩 옮겨 적으면 v3에서 넷이 늘어난 지금 한 곳을 빠뜨리는
 * 순간 "탄창을 쓰면 핀이 사라진다"가 되고, 그 버그는 타입이 잡지 않는다(필드가 빠진 것이 아니라 값이 빠진다).
 */
export function withMagazines(progress: Progress, magazines: MagazineState): Progress {
  return { ...progress, version: PROGRESS_VERSION, magazines }
}

/**
 * 핀·봉투·사물함을 바꾼 새 진행. 단계 기록도 탄창도 손대지 않는다.
 *
 * 셋을 한 패치로 받는 이유는 봉투 한 장이 셋을 **동시에** 움직이기 때문이다(핀이 빠지고 · 회차가 오르고 ·
 * 산출물이 들어온다). 셋을 따로 저장하면 그 사이에 저장이 한 번 실패할 수 있고, 그러면
 * "핀은 빠졌는데 산출물은 없다"가 남는다. 값의 규칙(상한·환급·소유)은 각자의 `economy/` 파일이 갖고
 * 여기서는 갈아 끼우기만 한다. 핀은 정규화 한 번 더 - 이 필드는 저장으로 곧장 가는 값이다.
 */
export function withEconomy(progress: Progress, patch: EconomyPatch): Progress {
  return {
    ...progress,
    version: PROGRESS_VERSION,
    pins: normalizePins(patch.pins ?? progress.pins),
    envelope: patch.envelope ?? progress.envelope,
    inventory: patch.inventory ?? progress.inventory,
  }
}

/**
 * 매일의 방 상태만 바꾼 새 진행. 단계 기록도 경제도 손대지 않는다.
 *
 * `withMagazines`·`withEconomy`와 같은 모양·같은 이유다 - 값의 규칙(연속 규칙 · 첫 결과만 · 상한)은 전부
 * `daily.ts`가 갖고 여기서는 갈아 끼우기만 한다. 스프레드로 나머지를 흘려보내는 것도 같은 이유다.
 */
export function withDaily(progress: Progress, daily: DailyState): Progress {
  return { ...progress, version: PROGRESS_VERSION, daily }
}

/** 안내를 마쳤다고 표시한 새 진행. `withDaily`와 같은 모양이다. */
export function withCoached(progress: Progress): Progress {
  return { ...progress, version: PROGRESS_VERSION, coached: true }
}

export function getLevelRecord(progress: Progress, levelId: number): LevelRecord | undefined {
  return progress.levels[levelKey(levelId)]
}

/**
 * 이 단계에 들어갈 수 있는가.
 *
 * **1단계는 언제나 열려 있다.** 그 외에는 직전 단계를 깼는지만 본다 -
 * 점수·시간은 해금 조건이 아니다. 튜토리얼의 목적은 통과이지 숙달이 아니기 때문이다.
 * 존재하지 않는 번호(0, 11, 소수)는 잠긴 것으로 본다.
 */
/**
 * 이 단계에 **다음으로 얹을 시도 번호**. 저장된 판 수(`plays`)가 그대로 그 값이다.
 *
 * 방은 단계마다 고정이고(9.5절 - 방을 외우는 것이 스킬) 판마다 갈리는 것은 변화뿐인데
 * (`gameState.changeSeedFor` = `${seed}:a${attempt}`), 그 시도 번호가 새로 들어올 때마다 0으로 돌아가면
 * **다시 들어올 때마다 같은 정답표**가 나온다. 한 번 외운 사람에게 그 단계는 그때부터 몇 초짜리이고,
 * 핀은 판마다 붙으므로(로드맵 3.1절) 그 반복은 그대로 핀 복사기다(사용자 신고).
 *
 * `plays`를 쓰는 이유는 그것이 **핀이 붙은 판의 수와 정확히 같기** 때문이다 - 둘 다 한 번의
 * `recordLevelResult`에서 오므로(`main.saveOutcome`이 기록과 핀을 같은 자리에서 낸다), 핀을 받은 판은
 * 반드시 다음 입장의 변화를 갈아 치운다. 저장에 새 값을 더하지 않은 것도 그 사실 덕이다(버전이 오르지 않는다).
 *
 * 기록이 없으면 0 - 첫 시도의 번호와 같은 값이다(`app/urlState.FIRST_ATTEMPT`. 방향 때문에 여기서 import 하지 않는다).
 */
export function nextAttemptFor(progress: Progress, levelId: number): number {
  return getLevelRecord(progress, levelId)?.plays ?? 0
}

export function isUnlocked(levelId: number, progress: Progress): boolean {
  if (getLevel(levelId) === undefined) return false
  if (levelId === FIRST_LEVEL_ID) return true
  return getLevelRecord(progress, levelId - 1)?.cleared === true
}

/** 지금 들어갈 수 있는 가장 높은 단계. 시작 화면의 기본 선택값이자 배경 방의 근거다. */
export function highestUnlockedLevelId(progress: Progress): number {
  let highest = FIRST_LEVEL_ID
  for (let id = FIRST_LEVEL_ID; isUnlocked(id, progress); id += 1) highest = id
  return highest
}

/** 깬 단계 수. 진척도 표시용. */
export function clearedCount(progress: Progress): number {
  return Object.values(progress.levels).filter((record) => record.cleared).length
}

/**
 * 앞에서부터 **연속으로** 깬 단계 수. 선택 화면의 사다리가 어디까지 왔는지가 이 값이다.
 *
 * `clearedCount`(전체 개수)와 다른 값인 이유는 `?level=9` 직행 때문이다 - 9단계만 깬 사람의
 * 전체 개수는 1이지만 사다리에서 "지금 할 단계"는 여전히 1단계다. 사다리가 보여야 하는 것은
 * 총합이 아니라 **연속으로 열린 길**이다.
 *
 * 해금 규칙(`isUnlocked`)과 같은 사실을 세므로 여기 함께 둔다. 화면 쪽에서 다시 세면
 * 규칙이 두 벌이 되고, 둘이 갈리는 날 "깼는데 다음 단계가 안 열린다"가 나온다.
 */
export function clearedPrefixCount(progress: Progress): number {
  let count = 0
  while (getLevel(FIRST_LEVEL_ID + count) !== undefined) {
    if (getLevelRecord(progress, FIRST_LEVEL_ID + count)?.cleared !== true) break
    count += 1
  }
  return count
}

/* --- 기록 갱신 (순수) --------------------------------------------------------- */

const EMPTY_RECORD: LevelRecord = {
  cleared: false,
  bestScore: 0,
  bestTimeMs: NO_TIME,
  bestFound: NOT_RECORDED,
  bestChangeCount: NOT_RECORDED,
  plays: 0,
}

/**
 * 한 판의 결과를 반영한 **새 진행**을 돌려준다. 입력은 건드리지 않는다(상태 불변 규칙).
 *
 * 갱신 규칙은 전부 "좋아지기만 한다"이다 - 클리어는 되돌아가지 않고, 최고 점수는 최댓값,
 * 최단 시간은 최솟값이다. 나중에 같은 단계를 대충 돌렸다고 기록이 깎이면
 * 플레이어는 이미 깬 단계를 다시 눌러 보지 않게 되고, 그 순간 연습 기능이 죽는다.
 */
export function recordLevelResult(
  progress: Progress,
  level: LevelDef,
  outcome: LevelOutcome,
): Progress {
  const key = levelKey(level.id)
  const before = progress.levels[key] ?? EMPTY_RECORD

  const foundCount = toNonNegative(outcome.foundCount, 0)
  const clearedNow = meetsClearCondition(level, foundCount)
  const score = toNonNegative(outcome.score, 0)
  const elapsedMs = finiteNonNegative(outcome.elapsedMs)

  const bestTimeMs =
    clearedNow && elapsedMs !== null
      ? Math.min(before.bestTimeMs ?? Number.POSITIVE_INFINITY, elapsedMs)
      : before.bestTimeMs

  /*
   * 발견 수·이상한 곳 수는 **최고 점수와 같은 판의 것**이라야 한다(`LevelRecord.bestFound`).
   * 그래서 각각 최댓값을 취하지 않고 이 판이 기록을 세웠을 때만 통째로 갈아 끼운다.
   * `>=`인 것은 첫 판을 위해서다 - 0점으로 시작하므로 `>`면 0점 판이 아무것도 못 남긴다.
   *
   * 옛 저장(이 필드가 없던 시절)은 기록을 다시 세우는 판에서 저절로 채워진다. 지금 판의
   * 값으로 먼저 메우지 않는 이유는 그러면 900점짜리 기록 옆에 이번 판의 발견 수가 붙어
   * **존재한 적 없는 판**이 한 줄로 만들어지기 때문이다.
   */
  const setsRecord = score >= before.bestScore
  const changeCount = toCountOrNull(outcome.changeCount)

  const after: LevelRecord = {
    cleared: before.cleared || clearedNow,
    bestScore: Math.max(before.bestScore, score),
    bestTimeMs,
    bestFound: setsRecord ? Math.floor(foundCount) : before.bestFound,
    bestChangeCount: setsRecord ? changeCount : before.bestChangeCount,
    plays: before.plays + 1,
  }

  // 탄창·핀·봉투·사물함은 이 함수의 관심사가 아니지만 **떨어뜨리면 안 된다** - 한 판 끝날 때마다
  // 사람이 가진 것이 사라지는 버그가 여기서 나온다. 그래서 스프레드다(`withMagazines`와 같은 이유).
  return {
    ...progress,
    version: PROGRESS_VERSION,
    levels: { ...progress.levels, [key]: after },
  }
}

/* --- 직렬화 ----------------------------------------------------------------- */

/** 저장된 단계 기록 하나를 읽는다. 형태가 아니면 `null` - 그 단계만 버리고 나머지는 살린다. */
function parseLevelRecord(value: unknown): LevelRecord | null {
  if (!isRecordLike(value)) return null

  return {
    cleared: value['cleared'] === true,
    bestScore: toNonNegative(value['bestScore'], 0),
    bestTimeMs: finiteNonNegative(value['bestTimeMs']) ?? NO_TIME,
    // 이 둘은 뒤늦게 생긴 필드다. 없는 저장(= 그때는 안 적었다)이 정상 입력이다.
    bestFound: toCountOrNull(value['bestFound']),
    bestChangeCount: toCountOrNull(value['bestChangeCount']),
    plays: Math.floor(toNonNegative(value['plays'], 0)),
  }
}

/**
 * 옛 버전의 단계 기록을 현재 포맷으로 올린다. 올릴 수 없으면 `null`(= 빈 진행으로 시작).
 *
 * **v1·v2·v3의 단계 기록은 모양이 같다.** 뒤 버전이 더한 것은 전부 형제 필드(`magazines` · 핀·봉투·사물함)
 * 이므로 여기서는 그대로 통과시킨다. 이 한 줄이 "탄창을 추가했더니 클리어 기록이 다 날아갔다"를 막는다.
 *
 * **미래 버전(우리가 모르는 포맷)도 통과시킨다.** 호출자(`parseProgress`)가 레코드 하나하나를
 * `parseLevelRecord`로 검사하므로 모양이 맞는 기록만 살고 나머지는 그 줄만 떨어진다. "조용히 틀린 진행"을
 * 막는 것은 버전 번호가 아니라 그 모양 검증이고, 이 규칙이 있어야 배포 직전에 열어 둔 옛 탭이 새 저장을
 * 읽어도 진행이 비지 않는다(머리말 "미래 버전의 단계 기록은 레코드 단위로 살린다"). 단계 기록의 모양이
 * 실제로 바뀌는 날에는 **그 버전 번호를 보고 여기서 갈라 준다** - 그때까지는 갈라 줄 것이 없다.
 *
 * 첫 버전보다 작은 번호(0·음수)는 우리가 쓴 적이 없다. 그것만 `null`이다.
 */
function migrateLevels(version: number, levels: unknown): unknown {
  if (version < FIRST_VERSION) return null
  return levels
}

/**
 * 옛 버전의 탄창을 현재 포맷으로 올린다.
 *
 * v1에는 필드 자체가 없다. 그때는 **초기 지급으로 채운다** - 0으로 두면 이미 플레이하던 사람만
 * 새 기능을 못 써 보게 되고, 그건 "기존 사용자에게 불리한 업데이트"라는 가장 피해야 할 모양이다.
 * 새로 시작하는 사람과 같은 출발선에 세우는 것이 맞다.
 */
function migrateMagazines(version: number, value: unknown): MagazineState {
  if (version <= VERSION_BEFORE_MAGAZINES) return createMagazineState()
  return parseMagazineState(value)
}

/**
 * 계정 시드를 읽는다. 없거나 비어 있으면 **발급한다.**
 *
 * v2 이하에는 필드가 없고, v3라도 기본 `mint`로 저장된 빈 시드가 있을 수 있다(머리말). 둘 다 같은 답이다 -
 * 지금 넘어온 `mint`로 채운다. 한 번 발급된 시드는 그대로 통과시킨다. 바뀌면 봉투 스트림이 통째로 바뀌어
 * "다음 것"이 달라지고, 그건 결정론의 약속을 어기는 것이다.
 */
function migrateAccountSeed(value: unknown, mint: AccountSeedMint): string {
  if (typeof value === 'string' && value !== UNMINTED_ACCOUNT_SEED) return value
  return mint()
}

/**
 * 저장 문자열 → 진행. **어떤 입력에도 던지지 않는다.**
 * 깨진 JSON·버전 없음·낯선 모양은 빈 진행으로 떨어진다. 미래 버전은 단계 기록을 레코드 단위로 살린다.
 *
 * `mint`는 계정 시드가 없을 때만 불린다. 기본값은 자리표시자다(`UNMINTED_ACCOUNT_SEED`) - 앱은 진짜를 넘긴다.
 */
export function parseProgress(raw: string | null, mint: AccountSeedMint = UNMINTED): Progress {
  if (raw === null || raw.trim() === '') return createEmptyProgress(mint)

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return createEmptyProgress(mint)
  }

  if (!isRecordLike(parsed)) return createEmptyProgress(mint)

  const version = parsed['version']
  if (typeof version !== 'number' || !Number.isInteger(version)) return createEmptyProgress(mint)

  const migrated = migrateLevels(version, parsed['levels'])
  if (!isRecordLike(migrated)) return createEmptyProgress(mint)

  const levels: Record<string, LevelRecord> = {}
  for (const [key, value] of Object.entries(migrated)) {
    // 십진수가 아닌 키는 우리가 쓴 것이 아니다. 지금 버리는 것이 나중에 터지는 것보다 싸다.
    if (!LEVEL_KEY_PATTERN.test(key)) continue
    const record = parseLevelRecord(value)
    if (record !== null) levels[key] = record
  }

  /*
   * v3의 넷은 각자의 파서가 "없거나 낯선 모양 → 새 계정 상태"를 맡는다. v2 저장(필드 없음)과 깨진 v3 저장이
   * 같은 경로다 - 탄창(`migrateMagazines`)처럼 버전을 볼 필요가 없는 것은 채울 값이 새 계정 상태 그 자체라서다.
   */
  return {
    version: PROGRESS_VERSION,
    levels,
    magazines: migrateMagazines(version, parsed['magazines']),
    accountSeed: migrateAccountSeed(parsed['accountSeed'], mint),
    pins: normalizePins(parsed['pins']),
    envelope: parseEnvelopeState(parsed['envelope']),
    inventory: parseInventory(parsed['inventory']),
    // v3 저장(필드 없음)도 여기로 온다 - 빈 상태가 곧 "아직 한 번도 안 했다"라서 버전을 볼 것이 없다(머리말).
    daily: parseDailyState(parsed['daily']),
    // `=== true`인 이유: 없거나 낯선 값은 전부 "아직 안 봤다"다. `Boolean(...)`이면 `'false'` 문자열이 참이 된다.
    coached: parsed['coached'] === true,
  }
}

/** 진행 → 저장 문자열. 버전은 **항상 현재 값**으로 쓴다(읽은 값을 되쓰지 않는다). */
export function serializeProgress(progress: Progress): string {
  return JSON.stringify({
    version: PROGRESS_VERSION,
    levels: progress.levels,
    magazines: progress.magazines,
    accountSeed: progress.accountSeed,
    pins: progress.pins,
    envelope: progress.envelope,
    inventory: progress.inventory,
    daily: progress.daily,
    coached: progress.coached,
  })
}

/* --- 저장소 입출력 ----------------------------------------------------------- */

/**
 * 진행을 읽는다. **실패는 조용히 빈 진행이 된다** -
 * 저장이 막힌 브라우저에서도 1단계부터 정상적으로 플레이할 수 있어야 한다.
 */
export function loadProgress(
  storage: ProgressStorage | null = defaultProgressStorage(),
  mint: AccountSeedMint = UNMINTED,
): Progress {
  if (storage === null) return createEmptyProgress(mint)

  try {
    return parseProgress(storage.getItem(PROGRESS_STORAGE_KEY), mint)
  } catch {
    return createEmptyProgress(mint)
  }
}

/**
 * 진행을 쓴다. 성공 여부를 **돌려준다** - 던지지 않는 대신 조용히 삼키지도 않는다.
 * 호출부는 이 값으로 "이 브라우저에서는 진행이 저장되지 않는다"를 한 번 알릴 수 있다.
 */
export function saveProgress(
  progress: Progress,
  storage: ProgressStorage | null = defaultProgressStorage(),
): boolean {
  if (storage === null) return false

  try {
    storage.setItem(PROGRESS_STORAGE_KEY, serializeProgress(progress))
    return true
  } catch {
    return false
  }
}

/** 진행을 지운다(설정의 "처음부터 다시"). 저장과 같은 이유로 던지지 않는다. */
export function clearProgress(
  storage: ProgressStorage | null = defaultProgressStorage(),
): boolean {
  if (storage === null) return false

  try {
    storage.removeItem(PROGRESS_STORAGE_KEY)
    return true
  } catch {
    return false
  }
}

/**
 * 한 판을 반영하고 곧바로 저장한다. 앱이 쓰는 입구이며, **저장 실패와 무관하게
 * 갱신된 진행을 돌려준다** - 이번 세션에서는 다음 단계로 넘어갈 수 있어야 하기 때문이다.
 */
export function commitLevelResult(
  progress: Progress,
  level: LevelDef,
  outcome: LevelOutcome,
  storage: ProgressStorage | null = defaultProgressStorage(),
): Readonly<{ progress: Progress; saved: boolean }> {
  const next = recordLevelResult(progress, level, outcome)
  return { progress: next, saved: saveProgress(next, storage) }
}

/**
 * 탄창을 갈아 끼우고 곧바로 저장한다. `commitLevelResult`와 같은 모양·같은 계약이다.
 *
 * 라운드 중에 쓴 탄창을 계정으로 되돌려 쓰는 것이 주 용도다. **저장 실패와 무관하게 갱신된 진행을
 * 돌려준다** - 저장이 막힌 브라우저에서도 이번 세션의 잔여 탄창은 정확해야 하기 때문이다.
 */
export function commitMagazines(
  progress: Progress,
  magazines: MagazineState,
  storage: ProgressStorage | null = defaultProgressStorage(),
): Readonly<{ progress: Progress; saved: boolean }> {
  const next = withMagazines(progress, magazines)
  return { progress: next, saved: saveProgress(next, storage) }
}

/**
 * 핀·봉투·사물함을 갈아 끼우고 곧바로 저장한다. 봉투 한 장의 결과 전부가 **이 한 번**으로 저장된다.
 * `commitLevelResult`·`commitMagazines`와 같은 모양·같은 계약이다 - 저장 실패와 무관하게 갱신된 진행을 돌려준다.
 */
export function commitEconomy(
  progress: Progress,
  patch: EconomyPatch,
  storage: ProgressStorage | null = defaultProgressStorage(),
): Readonly<{ progress: Progress; saved: boolean }> {
  const next = withEconomy(progress, patch)
  return { progress: next, saved: saveProgress(next, storage) }
}

/**
 * 매일의 방 상태를 갈아 끼우고 곧바로 저장한다. 위 셋과 같은 모양·같은 계약이다.
 *
 * 핀과 갈라 저장하는 것은 감수한다(`main.ts`의 `saveOutcome`이 단계 기록과 핀을 이미 갈라 저장한다) -
 * "출석은 됐는데 핀이 없다"는 다음 판이 메우지 않는 손해이나, 반대로 핀 때문에 그날의 기록이 미뤄지면
 * 새로고침 한 번에 그날의 판이 통째로 사라진다.
 */
export function commitDaily(
  progress: Progress,
  daily: DailyState,
  storage: ProgressStorage | null = defaultProgressStorage(),
): Readonly<{ progress: Progress; saved: boolean }> {
  const next = withDaily(progress, daily)
  return { progress: next, saved: saveProgress(next, storage) }
}

/**
 * 안내를 마쳤다. 저장이 막힌 브라우저에서는 `saved: false`로 돌아오고 **그래도 이번 판에서는 다시 뜨지 않는다** -
 * 메모리의 진행이 이미 참이기 때문이다. 판마다 다시 뜨는 것이 안내가 아예 없는 것보다 낫다(편의가 콘텐츠를 죽이지 않는다).
 */
export function commitCoached(
  progress: Progress,
  storage: ProgressStorage | null = defaultProgressStorage(),
): Readonly<{ progress: Progress; saved: boolean }> {
  const next = withCoached(progress)
  return { progress: next, saved: saveProgress(next, storage) }
}
